/**
 * Shared Dashboard Component
 *
 * This component provides the main dashboard UI that is shared between
 * the standalone app and the embeddable widget. It includes:
 * - Header with dynamic greeting
 * - Tab navigation (MDS / Care Assessments)
 * - Filter bar with search
 * - Assessment tables
 *
 * The widget can customize behavior via props (showHeader, onRowClick, api).
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Toaster } from "../ui/sonner";
import { toast } from "sonner";
import { Header } from "./Header";
import { HeaderBar } from "./HeaderBar";
import { MDSAssessmentsTable } from "./MDSAssessmentsTable";
import { MDSCalendarView } from "./MDSCalendarView";
import { exportMdsSummaryToPdf } from "../../utils/exportMdsSummaryPdf";
import { useDebounce } from "../../hooks/useDebounce";
import { CareAssessmentsTable } from "./CareAssessmentsTable";
import { ResidentsTable } from "./ResidentsTable";
import { ResidentDetailDialog } from "./ResidentDetailDialog";
import { Spinner } from "../ui/spinner";
import { Button } from "../ui/button";
import { AssessmentTab } from "../molecules/TabNavigation";
import { FilterState } from "../../types/dashboard";
import { STYLES } from "../../config/styles";
import { useWorkItemsAutoRefresh } from "../../hooks/useWorkItemsAutoRefresh";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { isInTransientOutage, transientErrorRefetchInterval } from "../../utils/transientErrorRefetch";
import { ApiError, isTransientError } from "../../services/apiError";
import type { ApiService } from "../../services/apiService";
import { apiService } from "../../services/apiService";
import { ApiServiceProvider, useApiService } from "../../contexts/ApiServiceContext";
import type { MDSRowClickData } from "./MDSAssessmentRow";
import type { CareRowClickData } from "./CareAssessmentRow";
import type { ResidentRowClickData } from "./ResidentRow";
import { trackCareQueueEvent } from "../../lib/tracking";
import type { WorkItem } from "../../types/workItems";
import type { ResidentData } from "../../types/dashboard";
import type { ResidentSummary } from "../../types/residents";
import { resolveResidentForPeriod } from "../../types/residents";
import type { RiskLevel } from "../../types/residents";
import { AddNoteDialog } from "./AddNoteDialog";
import { HideResidentDialog } from "./HideResidentDialog";
import { ConfigurationModal } from "./ConfigurationModal";
import { useFilterOptions } from "../../hooks/useFilterOptions";
import { List, CalendarDays, FileDown } from "lucide-react";

// Union type for row click data
export type RowClickData = MDSRowClickData | CareRowClickData | ResidentRowClickData;

export interface DashboardRowClickInfo {
  row: ResidentData;
  fullWorkItem?: WorkItem;
  tab: AssessmentTab;
}

export interface ResidentRowClickInfo {
  resident: ResidentSummary;
  tab: "residents";
}

interface DashboardProps {
  /** Whether to show the header with greeting (default: true) */
  showHeader?: boolean;
  /** Custom row click handler. If not provided, shows a toast notification */
  onRowClick?: (info: DashboardRowClickInfo) => void;
  /** Custom API service for authentication (used by widget) */
  api?: ApiService;
  /** Query key prefix to avoid cache conflicts (used by widget) */
  queryKeyPrefix?: string;
  /** When true, aggregates data across all facilities in the token */
  multiFacilityMode?: boolean;
}

const initialFilters: FilterState = {
  status: "",
  residentName: "",
  location: "",
  admissionDate: "",
  mdsOcType: "",
  referenceWindow: "",
  ardDate: "",
  // Default to no due-date pre-filter so the queue shows all items on load
  // (NETSOL-11598). Users narrow the window via the DateRangeFilter control.
  dueDate: "",
  wdsr02: "",
  assignedServices: "",
  nextAction: "",
  caaCompletion: "",
  requiredTransmissionDate: "",
  globalSearch: "",
  quickStatusFilters: [],
  payer: [],
  assessmentType: [],
  station: [],
  building: "all",
  mdsSection: "all",
  assignedOwner: "all",
  daysOpen: "",
  unsignedSections: [],
};

/**
 * NETSOL-12004: load a tab's persisted filters from sessionStorage so they
 * "stick" when the user switches tabs and back, and survive the host tearing
 * down and re-mounting the widget. Merged over initialFilters so any field
 * added after the value was persisted is always present (schema drift). Reads
 * are guarded — sessionStorage can be unavailable (private mode, sandboxed
 * iframe) and must degrade to the in-memory defaults.
 */
function loadPersistedFilters(storageKey: string): FilterState {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return initialFilters;
    const parsed = JSON.parse(raw) as Partial<FilterState>;
    return { ...initialFilters, ...parsed };
  } catch {
    return initialFilters;
  }
}

/**
 * Filter work items by unsigned sections (frontend-only filter).
 * If no sections selected, returns all items.
 * If sections selected, returns items that have ANY of the selected sections unsigned.
 */
function filterByUnsignedSections(
  workItems: WorkItem[],
  selectedSections: string[]
): WorkItem[] {
  if (selectedSections.length === 0) {
    return workItems;
  }

  return workItems.filter((item) => {
    const itemSections = item.unsigned_sections;
    if (!itemSections || itemSections.trim() === "") {
      return false;
    }
    // Parse the comma-separated sections (e.g., "A, B, C")
    const sections = itemSections.split(",").map((s) => s.trim().toUpperCase());
    // Match if item has ANY of the selected sections
    return selectedSections.some((selected) =>
      sections.includes(selected.toUpperCase())
    );
  });
}

export function Dashboard(props: DashboardProps) {
  const { api } = props;
  // Use provided API or default to main apiService. Provided above the Dashboard body so
  // every reader under it (the Header, the shared `me` query in useCurrentUser) uses it.
  const effectiveApi = useMemo(() => api || apiService, [api]);
  return (
    <ApiServiceProvider value={effectiveApi}>
      <DashboardContent {...props} />
    </ApiServiceProvider>
  );
}

function DashboardContent({
  showHeader = true,
  onRowClick,
  api,
  queryKeyPrefix = "work-items",
  multiFacilityMode = false,
}: DashboardProps) {
  // Detect widget context - api prop is only passed when used as widget
  const isWidget = !!api;

  const [activeTab, setActiveTab] = useState<AssessmentTab>("residents");

  // NETSOL-12004: persist each tab's filters in sessionStorage, scoped by
  // queryKeyPrefix so the standalone app and the embedded widget don't collide.
  const mdsFiltersStorageKey = `cq:${queryKeyPrefix}:mdsFilters`;
  const careFiltersStorageKey = `cq:${queryKeyPrefix}:careFilters`;
  const [mdsFilters, setMdsFilters] = useState<FilterState>(() =>
    loadPersistedFilters(mdsFiltersStorageKey)
  );
  const [careFilters, setCareFilters] = useState<FilterState>(() =>
    loadPersistedFilters(careFiltersStorageKey)
  );
  useEffect(() => {
    try {
      sessionStorage.setItem(mdsFiltersStorageKey, JSON.stringify(mdsFilters));
    } catch {
      // sessionStorage unavailable — filters stay in-memory only.
    }
  }, [mdsFilters, mdsFiltersStorageKey]);
  useEffect(() => {
    try {
      sessionStorage.setItem(careFiltersStorageKey, JSON.stringify(careFilters));
    } catch {
      // sessionStorage unavailable — filters stay in-memory only.
    }
  }, [careFilters, careFiltersStorageKey]);
  const [showRecentlyComplete, setShowRecentlyComplete] = useState(false);

  // NETSOL-12004 item #7: MDS assessments can be shown as the list table or a
  // month calendar keyed on the Assessment Reference Date.
  const [mdsView, setMdsView] = useState<"list" | "calendar">("list");

  // Residents tab state
  const [selectedResident, setSelectedResident] = useState<ResidentSummary | null>(null);
  const [residentDialogOpen, setResidentDialogOpen] = useState(false);
  const [residentsSearch, setResidentsSearch] = useState("");
  const [selectedRiskLevels, setSelectedRiskLevels] = useState<RiskLevel[]>([]);
  const [daysSinceReview, setDaysSinceReview] = useState("");
  const [showHiddenResidents, setShowHiddenResidents] = useState(false);
  // NETSOL-11791: "My Assignments" toggle — restrict residents to those
  // assigned to the current user. Defaults off (standard queue behavior).
  //
  // NETSOL-11843: persist the toggle in sessionStorage so it survives the host
  // tearing down and re-mounting the widget when the user navigates away from
  // Care Queue and back (the toggle used to silently reset to off). Scoped by
  // queryKeyPrefix so the standalone app and the embedded widget don't collide.
  // sessionStorage (not localStorage) so it's a per-session preference and never
  // outlives the browser tab. Reads/writes are guarded — sessionStorage can be
  // unavailable (private mode, sandboxed iframe) and must degrade to in-memory.
  const myAssignmentsStorageKey = `cq:${queryKeyPrefix}:myAssignments`;
  const [myAssignments, setMyAssignments] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(myAssignmentsStorageKey) === "true";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(myAssignmentsStorageKey, myAssignments ? "true" : "false");
    } catch {
      // sessionStorage unavailable — toggle stays in-memory only.
    }
  }, [myAssignments, myAssignmentsStorageKey]);
  const [selectedBuilding, setSelectedBuilding] = useState("all");
  const [selectedStation, setSelectedStation] = useState<string[]>([]);
  // Per-tab facility filter — matches how other filters (status, search) are scoped per tab
  const [mdsFacilityId, setMdsFacilityId] = useState<string>("all");
  const [careFacilityId, setCareFacilityId] = useState<string>("all");
  const [residentsFacilityId, setResidentsFacilityId] = useState<string>("all");
  const selectedFacilityId =
    activeTab === "mds" ? mdsFacilityId :
    activeTab === "care" ? careFacilityId :
    residentsFacilityId;
  const setSelectedFacilityId = (id: string) => {
    trackCareQueueEvent("care_queue_facility_changed", {
      facilityCount: id === "all" ? tokenFacilityIds.length : 1,
      mode: id === "all" ? "multi" : "single",
    });
    if (activeTab === "mds") setMdsFacilityId(id);
    else if (activeTab === "care") setCareFacilityId(id);
    else setResidentsFacilityId(id);
  };
  // Toggle facility grouping rows (only relevant in multi-facility mode)
  const [showFacilityGroups, setShowFacilityGroups] = useState(true);
  // Collapsed facility groups — lifted here so state survives ResidentsTable re-mounts
  const [collapsedFacilities, setCollapsedFacilities] = useState<Set<string>>(new Set());

  // Add Note dialog state
  const [addNoteResident, setAddNoteResident] = useState<ResidentSummary | null>(null);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);

  // Hide Resident dialog state
  const [hideResident, setHideResident] = useState<ResidentSummary | null>(null);
  const [hideDialogOpen, setHideDialogOpen] = useState(false);

  // Configuration modal state
  const [configModalOpen, setConfigModalOpen] = useState(false);

  // Debounce filters to prevent race conditions on rapid changes (300ms delay)
  const debouncedMdsFilters = useDebounce(mdsFilters, 300);
  const debouncedCareFilters = useDebounce(careFilters, 300);

  // Track tab change for triggering full refresh
  const [mdsTabChangeFlag, setMdsTabChangeFlag] = useState(false);
  const [careTabChangeFlag, setCareTabChangeFlag] = useState(false);
  const previousTabRef = useRef<AssessmentTab>(activeTab);

  // The client the Dashboard wrapper provides (the `api` prop, or the main apiService).
  const effectiveApi = useApiService();

  // Filter options for building/station dropdowns (residents tab)
  const filterOptions = useFilterOptions(effectiveApi);

  // Fetch user permissions (once per token: staleTime Infinity, setAuthToken invalidates).
  // NETSOL-11843: a failure (e.g. a 401 during token rotation, now that getPermissions
  // throws on non-OK) must NOT reset the license/lock to false — that would hide the
  // toggle and silently drop a provider security restriction. React Query keeps the last
  // successful answer through a failed refetch; only a failed first load falls back to the
  // built-in defaults below. LIIP-1036: after a 5xx or a network failure the query asks
  // again (bounded) and on a tab return, so first-load defaults do not stay for good.
  // onError as before (contract C12, frequency unchanged): one request per load (no retry;
  // the bounded repeats cover a 5xx), and a 5xx is reported by the load that starts the
  // outage only, not by the automatic repeats.
  const permissionsQuery = useQuery({
    queryKey: [`${queryKeyPrefix}-permissions`],
    queryFn: ({ client, queryKey }) =>
      effectiveApi.getPermissions({ reportServerError: !isInTransientOutage(client, queryKey) }),
    staleTime: Infinity,
    retry: false,
    refetchInterval: (query) => transientErrorRefetchInterval(query, false),
  });
  const perms = permissionsQuery.data?.CareQueuePermissions;
  const canDismissWorkItems = perms?.CanDismissWorkItems ?? false;
  const canViewResidentTab = perms?.CanViewResidentTab ?? true;
  const canViewMDSTab = perms?.CanViewMDSTab ?? true;
  const canViewCareAssessmentTab = perms?.CanViewCareAssessmentTab ?? true;
  // NETSOL-11790: provider security setting forcing the queue to assigned residents.
  const restrictToAssignedResidents = perms?.RestrictToAssignedResidents ?? false;
  // NETSOL-11843: whether the facility is licensed for My Assignments. Defaults
  // false so the toggle stays hidden until licensing is confirmed.
  const myAssignmentsLicensed = perms?.MyAssignmentsLicensed ?? false;
  // NETSOL-11843: has the permissions call resolved at least once? Gates the
  // residents query so it never fires with a not-yet-known license/restriction
  // state — otherwise a security-restricted user briefly sees the full,
  // unfiltered facility while /permissions is still in flight. Also true after a
  // failed first load, so the residents query is not blocked forever; the backend
  // independently enforces the restriction. isFetched stays true through a refetch
  // (status goes back to "pending" while a query with no data refetches), so the
  // permissions repeats after a failed first load do not toggle the residents query.
  const permissionsLoaded = permissionsQuery.isFetched;

  // NETSOL-11790: when the provider security setting restricts to assigned
  // residents, force the My Assignments filter on (and lock the toggle) so the
  // queue always shows only the user's assigned residents.
  //
  // NETSOL-11843: the user's own toggle only takes effect when the feature is
  // actually licensed. This matters now that the toggle is persisted: a value of
  // `true` carried over from a licensed facility must not silently filter the
  // queue at a facility where the feature is unlicensed and the toggle is hidden.
  const effectiveMyAssignments =
    restrictToAssignedResidents || (myAssignmentsLicensed && myAssignments);

  // NETSOL-11843: show the "My Assignments" toggle only when the feature is
  // licensed. A provider security lock (NETSOL-11790) implies the feature is in
  // use, so the locked toggle is always surfaced regardless of the license read.
  const showMyAssignmentsToggle = myAssignmentsLicensed || restrictToAssignedResidents;

  // Visible tabs based on permissions
  const visibleTabs = useMemo(() => {
    const tabs: AssessmentTab[] = [];
    if (canViewResidentTab) tabs.push("residents");
    if (canViewMDSTab) tabs.push("mds");
    if (canViewCareAssessmentTab) tabs.push("care");
    return tabs;
  }, [canViewResidentTab, canViewMDSTab, canViewCareAssessmentTab]);

  // If the active tab is hidden by permissions, switch to the first visible tab
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [visibleTabs, activeTab]);

  // Reset tab change flags after render
  useEffect(() => {
    if (mdsTabChangeFlag) {
      setMdsTabChangeFlag(false);
    }
    if (careTabChangeFlag) {
      setCareTabChangeFlag(false);
    }
  }, [mdsTabChangeFlag, careTabChangeFlag]);

  // Current filters based on active tab
  const currentFilters = activeTab === "mds" ? mdsFilters : careFilters;
  const setCurrentFilters = activeTab === "mds" ? setMdsFilters : setCareFilters;

  // MDS assessments data
  const {
    workItems: mdsWorkItems,
    isLoading: mdsLoading,
    isRefreshing: mdsRefreshing,
    error: mdsError,
    lastRefreshedAt: mdsLastRefreshed,
    refresh: refreshMds,
  } = useWorkItemsAutoRefresh({
    filters: debouncedMdsFilters,
    tab: "mds",
    isTabChange: mdsTabChangeFlag,
    enabled: activeTab === "mds",
    api: effectiveApi,
    queryKeyPrefix: `${queryKeyPrefix}-mds`,
    facilityId: mdsFacilityId !== "all" ? mdsFacilityId : undefined,
  });

  // Care assessments data
  const {
    workItems: careWorkItems,
    isLoading: careLoading,
    isRefreshing: careRefreshing,
    error: careError,
    lastRefreshedAt: careLastRefreshed,
    refresh: refreshCare,
  } = useWorkItemsAutoRefresh({
    filters: debouncedCareFilters,
    tab: "care",
    isTabChange: careTabChangeFlag,
    enabled: activeTab === "care",
    api: effectiveApi,
    queryKeyPrefix: `${queryKeyPrefix}-care`,
    facilityId: careFacilityId !== "all" ? careFacilityId : undefined,
  });

  // Token identity — fetched instantly from /api/auth/me (no EHR calls).
  // Always enabled: multi-facility mode needs it for facility list,
  // and the scheduler config tab needs clientId + facilityId(s).
  // One `me` query shared with the Header (one request per token, the hook picks the
  // client); it recovers by itself after a 5xx or a network failure. On an error meData
  // stays empty.
  const { data: meData } = useCurrentUser();
  // Parse comma-separated facilityIds claim into an array
  const tokenFacilityIds = useMemo(() => {
    const raw = meData?.facilityIds || meData?.facilityId || "";
    return raw ? raw.split(",").map((s: string) => s.trim()).filter(Boolean) : [];
  }, [meData]);
  // Client ID from token (used by scheduler config tab)
  const tokenClientId = meData?.clientId || "";

  // Facility list for multi-facility filter.
  // Non-fatal: a 4xx or an unreadable answer gives [] (the dropdown uses the token ids).
  // LIIP-1036: a 5xx or a network failure is a transient error instead, so the list asks
  // again (bounded, and on a tab return) until the API is back. While it is in that error
  // the dropdown keeps the last list, or the token ids; no new message is shown.
  const { data: facilityListData } = useQuery({
    queryKey: [`${queryKeyPrefix}-facility-list`],
    queryFn: async () => {
      try {
        const response = await effectiveApi.fetch("/api/proxy/ehr/hierarchy/facility-list");
        if (response.status >= 500) {
          throw new ApiError(`Failed to fetch facility list: ${response.statusText}`, response.status);
        }
        if (!response.ok) return [];
        const data = await response.json();
        // Response shape: { success, message, data: [...] }
        return Array.isArray(data) ? data : (data?.data || data?.facilities || []);
      } catch (error) {
        if (isTransientError(error)) throw error;
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
    // No retry: the bounded repeats ask again, one request per repeat (PR #740 review item 9).
    retry: false,
    refetchInterval: (query) => transientErrorRefetchInterval(query, false),
  });

  // Fetch UI defaults for all facilities (dict keyed by facility_id).
  // Non-fatal: a 4xx gives null and the widget falls back to Pydantic defaults. A 5xx or
  // a network failure is a transient error (LIIP-1036): the query asks again until the API
  // is back, and the widget uses the last answer or the defaults meanwhile.
  const { data: uiDefaultsMap } = useQuery({
    queryKey: [`${queryKeyPrefix}-ui-defaults`],
    queryFn: () => effectiveApi.getUiDefaults(),
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchInterval: (query) => transientErrorRefetchInterval(query, false),
  });

  // Fetch disabled risk factors from risk config (per-facility map).
  // Same rules as ui-defaults: a 4xx gives null (no factors disabled); a transient
  // failure asks again until the API is back, one request per repeat (no retry).
  const { data: riskConfigMap } = useQuery({
    queryKey: [`${queryKeyPrefix}-risk-config`],
    queryFn: () => effectiveApi.getRiskFactorConfig(),
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchInterval: (query) => transientErrorRefetchInterval(query, false),
  });
  // When a specific facility is selected, use its disabled factors.
  // When "all", don't disable any factors — each facility may differ.
  const disabledRiskFactors =
    (selectedFacilityId !== "all"
      ? riskConfigMap?.[selectedFacilityId]?.custom_config?.disabled_risk_factors
      : []
    ) ?? [];

  // Pick defaults for the currently selected facility; undefined when "all" is selected.
  const activeFacilityDefaults =
    selectedFacilityId !== "all" ? uiDefaultsMap?.[selectedFacilityId] : undefined;


  // Lookback period for New Insights columns
  const [insightsLookback, setInsightsLookback] = useState(3);

  // Residents data
  // Pass facility filter when the user has selected a specific facility.
  // When "all" is selected, omit the filter so the backend returns all accessible facilities.
  const facilityFilter = residentsFacilityId !== "all" ? [residentsFacilityId] : undefined;
  const {
    data: residentsData,
    isLoading: residentsLoading,
    error: residentsError,
    dataUpdatedAt: residentsUpdatedAt,
    refetch: refetchResidents,
  } = useQuery({
    // myAssignments is part of the key so toggling refetches; the 60s
    // refetchInterval also picks up assignment changes (AC #5/#6/#7).
    // effectiveMyAssignments folds in the provider security restriction
    // (NETSOL-11790) so the filter applies even when the toggle is off.
    queryKey: [`${queryKeyPrefix}-residents`, showHiddenResidents, residentsFacilityId, multiFacilityMode, effectiveMyAssignments],
    // A 5xx is reported by the load that opens a transient outage window only, not by the
    // automatic repeats inside it (PR #740 review item 5; contract C12).
    queryFn: async ({ client, queryKey }) => {
      return effectiveApi.getResidentsSummary(
        showHiddenResidents || undefined,
        undefined,
        facilityFilter,
        effectiveMyAssignments || undefined,
        { reportServerError: !isInTransientOutage(client, queryKey) }
      );
    },
    // NETSOL-11843: wait for /permissions before the first fetch so a
    // security-restricted user never sees the full facility while the
    // license/restriction state is still unknown (effectiveMyAssignments would
    // otherwise be false on the very first request).
    enabled: activeTab === "residents" && permissionsLoaded,
    staleTime: 60 * 1000, // 1 minute
    // Auto-refresh every minute; after a 5xx or network failure ask again sooner
    // (bounded) so "Error loading residents" clears once the API is back.
    refetchInterval: (query) => transientErrorRefetchInterval(query, 60 * 1000),
    // No retry: the bounded repeats ask again after a transient error, one request per
    // repeat (PR #740 review item 9); a 401 waits for the host's new token.
    retry: false,
    // NETSOL-11843: keep the current list on screen while a new query runs
    // (toggling My Assignments, changing facility, or the 60s auto-refresh)
    // instead of collapsing to a full-page spinner or a blank error. The list
    // stays visible and swaps in place once the filtered data arrives.
    placeholderData: keepPreviousData,
  });

  const residents = residentsData?.residents || [];
  const residentsLastRefreshed = residentsUpdatedAt ? new Date(residentsUpdatedAt) : null;

  // NETSOL-11843: with keepPreviousData the last successful list stays in
  // residentsData through a failed refetch, so only surface the full error
  // state when there is genuinely nothing to show. Otherwise keep the (stale)
  // list on screen — a transient auto-refresh error must not blank the queue.
  const showResidentsError = !!residentsError && residents.length === 0;
  const showResidentsList = !showResidentsError;

  // Facility options for the filter dropdown.
  // Facility options priority:
  // 1. EHR facility-list API (has names)
  // 2. Loaded data (residents/MDS/Care — available after first tab visit)
  // 3. Token facilityIds claim — available immediately, IDs only
  const facilityOptions = useMemo(() => {
    if (facilityListData && Array.isArray(facilityListData) && facilityListData.length > 0) {
      return facilityListData
        .map((f: { facilityCode?: string; facilityGuid?: string; facilityId?: string; facility_id?: string; facilityName?: string; facility_name?: string; name?: string }) => ({
          id: f.facilityCode || f.facilityId || f.facility_id || f.facilityGuid || "",
          name: f.facilityName || f.facility_name || f.name || f.facilityCode || f.facilityId || f.facility_id || "",
        }))
        .filter((f: { id: string }) => f.id);
    }
    // Fallback: use token facility IDs (stable, unaffected by data filters)
    return tokenFacilityIds.map((id) => ({ id, name: id }));
  }, [facilityListData, tokenFacilityIds]);

  const facilityNameMap = useMemo(
    () => Object.fromEntries(facilityOptions.map((f) => [f.id, f.name])),
    [facilityOptions]
  );

  // Resolve period-specific risk data based on selected lookback. Shared with
  // the resident-detail widget via resolveResidentForPeriod so both surfaces
  // show the same window-adjusted risk level.
  const resolvedResidents = useMemo(
    () => residents.map((r) => resolveResidentForPeriod(r, insightsLookback)),
    [residents, insightsLookback]
  );

  // Filter residents by search term, risk level, and days since review
  const filteredResidents = useMemo(() => {
    let result = resolvedResidents;

    // Text search filter
    if (residentsSearch.trim()) {
      const search = residentsSearch.toLowerCase();
      result = result.filter(
        (r) =>
          r.resident_name.toLowerCase().includes(search) ||
          (r.room && r.room.toLowerCase().includes(search))
      );
    }

    // Risk level filter (if any selected, only show those levels)
    if (selectedRiskLevels.length > 0) {
      result = result.filter((r) => selectedRiskLevels.includes(r.risk_level));
    }

    // Days since last review filter
    if (daysSinceReview.trim()) {
      const minDays = parseInt(daysSinceReview, 10);
      if (!isNaN(minDays)) {
        result = result.filter(
          (r) => r.days_since_last_review != null && r.days_since_last_review >= minDays
        );
      }
    }

    // Building filter
    if (selectedBuilding && selectedBuilding !== "all") {
      result = result.filter((r) => r.building_id === selectedBuilding);
    }

    // Station filter
    if (selectedStation && selectedStation.length > 0) {
      result = result.filter((r) => r.station_id && selectedStation.includes(r.station_id));
    }

    return result;
  }, [resolvedResidents, residentsSearch, selectedRiskLevels, daysSinceReview, selectedBuilding, selectedStation]);

  // Compute counts by risk level
  const riskCounts = useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    filteredResidents.forEach((r) => {
      if (r.risk_level === "HIGH") counts.high++;
      else if (r.risk_level === "MEDIUM") counts.medium++;
      else if (r.risk_level === "LOW") counts.low++;
    });
    return counts;
  }, [filteredResidents]);

  // Apply frontend-only filters (unsigned sections for MDS)
  const filteredMdsWorkItems = useMemo(() => {
    return filterByUnsignedSections(mdsWorkItems, mdsFilters.unsignedSections);
  }, [mdsWorkItems, mdsFilters.unsignedSections]);

  // Current tab data
  const workItems = activeTab === "mds" ? filteredMdsWorkItems : careWorkItems;
  const isLoading = activeTab === "mds" ? mdsLoading : careLoading;
  const isRefreshing = activeTab === "mds" ? mdsRefreshing : careRefreshing;
  const error = activeTab === "mds" ? mdsError : careError;
  const lastRefreshedAt = activeTab === "mds" ? mdsLastRefreshed : careLastRefreshed;
  const retryAssessments = activeTab === "mds" ? refreshMds : refreshCare;
  const resultCount = workItems.length;

  const handleFilterChange = (field: keyof FilterState, value: string | string[]) => {
    // Determine value shape for analytics
    const isEmpty =
      value === "" || value === "all" || (Array.isArray(value) && value.length === 0);
    if (isEmpty) {
      trackCareQueueEvent("care_queue_filter_cleared", { filterType: String(field) });
    } else {
      const valueShape = Array.isArray(value)
        ? `multi_select_${value.length}_items`
        : "single_select";
      trackCareQueueEvent("care_queue_filter_applied", {
        filterType: String(field),
        valueShape,
      });
    }
    setCurrentFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyAllFilters = (filters: FilterState) => {
    trackCareQueueEvent("care_queue_filter_applied", {
      filterType: "bulk_apply",
      valueShape: "bulk",
    });
    setCurrentFilters(filters);
  };

  const handleClearAllFilters = () => {
    trackCareQueueEvent("care_queue_filter_cleared", { filterType: "all" });
    setCurrentFilters(initialFilters);
  };

  const handleTabChange = (tab: AssessmentTab) => {
    if (tab !== previousTabRef.current) {
      trackCareQueueEvent("care_queue_view_switched", {
        from: previousTabRef.current,
        to: tab,
      });
      if (tab === "mds") {
        setMdsTabChangeFlag(true);
      } else if (tab === "care") {
        setCareTabChangeFlag(true);
      }
      previousTabRef.current = tab;
    }

    setActiveTab(tab);
    // Keep filters - each tab remembers its last filters
  };

  // Resident row click handler
  const handleResidentRowClick = (data: ResidentRowClickData) => {
    setSelectedResident(data.resident);
    setResidentDialogOpen(true);
  };

  // Add Note handler
  const handleAddNote = (resident: ResidentSummary) => {
    setAddNoteResident(resident);
    setAddNoteDialogOpen(true);
  };

  // Hide Resident handler
  const handleHideResident = (resident: ResidentSummary) => {
    setHideResident(resident);
    setHideDialogOpen(true);
  };

  // Review Completed handler
  const queryClient = useQueryClient();
  const reviewMutation = useMutation({
    mutationFn: async (resident: ResidentSummary) => {
      return effectiveApi.markResidentReviewed(resident.resident_id, {
        reviewed_by: "current-user",
        action_taken: "NO_ACTION",
      }, resident.wq_id);
    },
    onSuccess: (_data, resident) => {
      queryClient.invalidateQueries({
        queryKey: [`${queryKeyPrefix}-residents`],
      });
      toast.success(`Review completed for ${resident.resident_name}`);
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast.error("Failed to mark review completed", { description: message });
    },
  });

  const handleReviewCompleted = (resident: ResidentSummary) => {
    trackCareQueueEvent("care_queue_review_completed", {
      residentId: String(resident.resident_id),
    });
    reviewMutation.mutate(resident);
  };

  // Row click handlers
  const handleMDSRowClick = (data: MDSRowClickData) => {
    if (onRowClick) {
      onRowClick({ ...data, tab: "mds" });
    } else {
      const workItemId = data.row.work_item_id || "";
      toast(`Opening MDS Assessment ${workItemId}`, {
        description: "Work item details",
      });
    }
  };

  const handleCareRowClick = (data: CareRowClickData) => {
    if (onRowClick) {
      onRowClick({ ...data, tab: "care" });
    } else {
      const workItemId = data.row.work_item_id || "";
      toast(`Opening Care Assessment ${workItemId}`, {
        description: "Work item details",
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Only render Toaster in main app, NOT in widget (Sonner injects to document.head) */}
      {!isWidget && <Toaster />}

      {/* Header - Outside the card, on page background */}
      {showHeader && (
        <Header
          summaryData={activeTab === "residents" ? {
            totalCount: filteredResidents.length,
            highRiskCount: riskCounts.high,
            mediumRiskCount: riskCounts.medium,
            lowRiskCount: riskCounts.low,
          } : undefined}
        />
      )}

      {/* Main content card */}
      <div className="bg-white rounded-lg p-2 mt-2 flex flex-col gap-1 flex-1 min-h-0">

      {/* Header Bar with Tabs, Search, and Filter */}
      <HeaderBar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        visibleTabs={visibleTabs}
        filters={currentFilters}
        onFilterChange={handleFilterChange}
        onClearAllFilters={handleClearAllFilters}
        onApplyAllFilters={handleApplyAllFilters}
        resultCount={resultCount}
        showRecentlyComplete={showRecentlyComplete}
        onShowRecentlyCompleteChange={(val: boolean) => {
          trackCareQueueEvent(val ? "care_queue_filter_applied" : "care_queue_filter_cleared", {
            filterType: "recently_complete",
            ...(val ? { valueShape: "boolean" } : {}),
          });
          setShowRecentlyComplete(val);
        }}
        api={effectiveApi}
        residentsSearch={residentsSearch}
        onResidentsSearchChange={(val: string) => {
          if (val.trim()) {
            trackCareQueueEvent("care_queue_filter_applied", { filterType: "search", valueShape: "text_search" });
          } else if (residentsSearch.trim()) {
            trackCareQueueEvent("care_queue_filter_cleared", { filterType: "search" });
          }
          setResidentsSearch(val);
        }}
        selectedRiskLevels={selectedRiskLevels}
        onRiskLevelsChange={(val: RiskLevel[]) => {
          if (val.length > 0) {
            trackCareQueueEvent("care_queue_filter_applied", { filterType: "risk_level", valueShape: `multi_select_${val.length}_items` });
          } else {
            trackCareQueueEvent("care_queue_filter_cleared", { filterType: "risk_level" });
          }
          setSelectedRiskLevels(val);
        }}
        daysSinceReview={daysSinceReview}
        onDaysSinceReviewChange={(val: string) => {
          if (val && val !== "") {
            trackCareQueueEvent("care_queue_filter_applied", { filterType: "days_since_review", valueShape: "single_select" });
          } else {
            trackCareQueueEvent("care_queue_filter_cleared", { filterType: "days_since_review" });
          }
          setDaysSinceReview(val);
        }}
        showHiddenResidents={showHiddenResidents}
        onShowHiddenChange={(val: boolean) => {
          trackCareQueueEvent(val ? "care_queue_filter_applied" : "care_queue_filter_cleared", {
            filterType: "show_hidden",
            ...(val ? { valueShape: "boolean" } : {}),
          });
          setShowHiddenResidents(val);
        }}
        myAssignments={effectiveMyAssignments}
        myAssignmentsAvailable={showMyAssignmentsToggle}
        myAssignmentsLocked={restrictToAssignedResidents}
        onMyAssignmentsChange={(val: boolean) => {
          trackCareQueueEvent(val ? "care_queue_filter_applied" : "care_queue_filter_cleared", {
            filterType: "my_assignments",
            ...(val ? { valueShape: "boolean" } : {}),
          });
          setMyAssignments(val);
        }}
        onSettingsClick={() => setConfigModalOpen(true)}
        selectedFacilityId={selectedFacilityId}
        onFacilityChange={setSelectedFacilityId}
        facilityOptions={facilityOptions}
        selectedBuilding={selectedBuilding}
        onBuildingChange={(val: string) => {
          if (val && val !== "all") {
            trackCareQueueEvent("care_queue_filter_applied", { filterType: "building", valueShape: "single_select" });
          } else {
            trackCareQueueEvent("care_queue_filter_cleared", { filterType: "building" });
          }
          setSelectedBuilding(val);
        }}
        buildingOptions={filterOptions.buildings}
        selectedStation={selectedStation}
        onStationChange={(val: string[]) => {
          if (val.length > 0) {
            trackCareQueueEvent("care_queue_filter_applied", { filterType: "station", valueShape: `multi_select_${val.length}_items` });
          } else {
            trackCareQueueEvent("care_queue_filter_cleared", { filterType: "station" });
          }
          setSelectedStation(val);
        }}
        stationOptions={filterOptions.stations}
        multiFacilityMode={multiFacilityMode}
      />

      {/* Active Filter Badges for Residents tab are now rendered inline in HeaderBar */}

      {/* Active Filter Badges for MDS/Care tabs are now rendered inline in HeaderBar */}

      {/* Tab Content */}
      <div className={`flex flex-col flex-1 min-h-0 ${STYLES.spacing.subsection}`}>

        {/* No accessible tabs */}
        {visibleTabs.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <span className="text-muted-foreground">
              You do not have permission to view any tabs. Please contact your administrator.
            </span>
          </div>
        )}

        {/* Residents Tab Content */}
        {activeTab === "residents" && canViewResidentTab && (
          <>

            {/* Loading State */}
            {residentsLoading && (
              <Spinner label="Loading Residents..." />
            )}

            {/* Error State — only when there's no list to fall back to */}
            {!residentsLoading && showResidentsError && (
              <div className="flex items-center justify-center gap-3 py-12">
                <span className="text-destructive">
                  Error loading residents:{" "}
                  {residentsError instanceof Error ? residentsError.message : "Unknown error"}
                </span>
                {/* The view also asks again by itself (bounded); Retry is for a user who does not want to wait.
                    A click while a request is running joins it (no second request). */}
                <Button variant="outline" size="sm" onClick={() => void refetchResidents({ cancelRefetch: false })}>
                  Retry
                </Button>
              </div>
            )}

            {/* Residents Table */}
            {!residentsLoading && showResidentsList && (
              <ResidentsTable
                residents={filteredResidents}
                onRowClick={handleResidentRowClick}
                onAddNote={handleAddNote}
                onReviewCompleted={handleReviewCompleted}
                onHideResident={handleHideResident}
                insightsLookback={insightsLookback}
                onInsightsLookbackChange={(days: number) => {
                  trackCareQueueEvent("care_queue_trend_window_changed", {
                    from: insightsLookback,
                    to: days,
                  });
                  setInsightsLookback(days);
                }}
                groupByFacility={multiFacilityMode && showFacilityGroups}
                hiddenRiskFactors={[
                  ...(activeFacilityDefaults?.hidden_risk_factors ?? []),
                  ...disabledRiskFactors,
                ]}
                facilityNameMap={facilityNameMap}
                collapsedFacilities={collapsedFacilities}
                onCollapsedFacilitiesChange={setCollapsedFacilities}
              />
            )}

            {/* Footer Panel */}
            {!residentsLoading && showResidentsList && (
              <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-t border-border text-sm text-muted-foreground shrink-0">
                <div className="flex items-center gap-4">
                  <span>
                    Showing {filteredResidents.length} Resident{filteredResidents.length !== 1 ? "s" : ""}
                  </span>
                  {multiFacilityMode && (
                    <button
                      onClick={() => setShowFacilityGroups(v => !v)}
                      className="text-xs underline underline-offset-2 hover:text-foreground transition-colors"
                    >
                      {showFacilityGroups ? "Hide facility groups" : "Show facility groups"}
                    </button>
                  )}
                </div>
                {residentsLastRefreshed && (
                  <span>Last updated: {residentsLastRefreshed.toLocaleTimeString()}</span>
                )}
              </div>
            )}

            {/* Resident Detail Dialog */}
            <ResidentDetailDialog
              resident={selectedResident}
              open={residentDialogOpen}
              onOpenChange={setResidentDialogOpen}
              initialInsightsDays={insightsLookback}
              disabledRiskFactors={disabledRiskFactors}
              aiSummaryEnabled={
                // Resolve per the resident's own facility so the toggle works
                // even when the dashboard filter is "All Facilities". Falls
                // back to the active filter's defaults, then to false.
                (selectedResident?.facility_id
                  ? uiDefaultsMap?.[selectedResident.facility_id]?.ai_summary_enabled
                  : activeFacilityDefaults?.ai_summary_enabled) ?? false
              }
              aiReasoningVisible={
                (selectedResident?.facility_id
                  ? uiDefaultsMap?.[selectedResident.facility_id]?.ai_reasoning_visible
                  : activeFacilityDefaults?.ai_reasoning_visible) ?? false
              }
            />

            {/* Add Note Dialog */}
            <AddNoteDialog
              resident={addNoteResident}
              open={addNoteDialogOpen}
              onOpenChange={setAddNoteDialogOpen}
            />

            {/* Hide Resident Dialog */}
            <HideResidentDialog
              resident={hideResident}
              open={hideDialogOpen}
              onOpenChange={setHideDialogOpen}
            />

            {/* Configuration Modal */}
            <ConfigurationModal
              open={configModalOpen}
              onOpenChange={setConfigModalOpen}
              api={api}
              clientId={tokenClientId}
              facilityOptions={facilityOptions}
            />
          </>
        )}

        {/* MDS/Care Tab Content */}
        {((activeTab === "mds" && canViewMDSTab) || (activeTab === "care" && canViewCareAssessmentTab)) && (
          <>
            {/* Loading State */}
            {isLoading && (
              <Spinner label={`Loading ${activeTab === "mds" ? "MDS" : "Care"} Assessments...`} />
            )}

            {/* Refreshing State */}
            {isRefreshing && !isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="animate-pulse">Refreshing...</span>
                {lastRefreshedAt && (
                  <span>Last updated: {lastRefreshedAt.toLocaleTimeString()}</span>
                )}
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center gap-3 py-12">
                <span className="text-destructive">
                  Error loading assessments:{" "}
                  {error instanceof Error ? error.message : "Unknown error"}
                </span>
                {/* The view also asks again by itself (bounded); Retry is for a user who does not want to wait. */}
                <Button variant="outline" size="sm" onClick={retryAssessments}>
                  Retry
                </Button>
              </div>
            )}

            {/* MDS Toolbar: List/Calendar toggle + Export to PDF (NETSOL-12004 #4/#7) */}
            {activeTab === "mds" && !isLoading && !error && (
              <div className="flex items-center justify-between gap-2 shrink-0">
                <div className="inline-flex rounded-[var(--radius)] border border-border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setMdsView("list")}
                    aria-pressed={mdsView === "list"}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                      mdsView === "list"
                        ? "bg-primary text-primary-foreground"
                        : "bg-transparent text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <List className="h-4 w-4" />
                    List
                  </button>
                  <button
                    type="button"
                    onClick={() => setMdsView("calendar")}
                    aria-pressed={mdsView === "calendar"}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                      mdsView === "calendar"
                        ? "bg-primary text-primary-foreground"
                        : "bg-transparent text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <CalendarDays className="h-4 w-4" />
                    Calendar
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => exportMdsSummaryToPdf(filteredMdsWorkItems)}
                  className="inline-flex items-center gap-1.5 rounded-[var(--radius)] border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <FileDown className="h-4 w-4" />
                  Export to PDF
                </button>
              </div>
            )}

            {/* MDS Table */}
            {activeTab === "mds" && !isLoading && !error && mdsView === "list" && (
              <MDSAssessmentsTable workItems={filteredMdsWorkItems} onRowClick={handleMDSRowClick} canDismissWorkItems={canDismissWorkItems} groupByFacility={multiFacilityMode && showFacilityGroups} facilityNameMap={facilityNameMap} />
            )}

            {/* MDS Calendar */}
            {activeTab === "mds" && !isLoading && !error && mdsView === "calendar" && (
              <MDSCalendarView workItems={filteredMdsWorkItems} onItemClick={handleMDSRowClick} />
            )}

            {/* Care Table */}
            {activeTab === "care" && !isLoading && !error && (
              <CareAssessmentsTable workItems={careWorkItems} onRowClick={handleCareRowClick} canDismissWorkItems={canDismissWorkItems} groupByFacility={multiFacilityMode && showFacilityGroups} facilityNameMap={facilityNameMap} />
            )}

            {/* Footer Panel */}
            {!isLoading && !error && (
              <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-t border-border text-sm text-muted-foreground shrink-0">
                <div className="flex items-center gap-4">
                  <span>
                    Showing {resultCount} {activeTab === "mds" ? "MDS" : "Care"} assessment{resultCount !== 1 ? "s" : ""}
                  </span>
                  {multiFacilityMode && (
                    <button
                      onClick={() => setShowFacilityGroups(v => !v)}
                      className="text-xs underline underline-offset-2 hover:text-foreground transition-colors"
                    >
                      {showFacilityGroups ? "Hide facility groups" : "Show facility groups"}
                    </button>
                  )}
                </div>
                {lastRefreshedAt && (
                  <span>Last updated: {lastRefreshedAt.toLocaleTimeString()}</span>
                )}
              </div>
            )}
          </>
        )}

      </div>
      </div>{/* end main content card */}
    </div>
  );
}

export default Dashboard;
